// ══════════════════════════════════════════════════════════════════
//  ErrorBoundary — captura erros de render e evita tela branca.
//  Usado globalmente (App) e por rota (em volta do <Outlet> do Layout,
//  pra um crash de página não derrubar a navegação).
// ══════════════════════════════════════════════════════════════════
import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle , RefreshCw } from 'lucide-react';
import { reportError } from '@lib/sentry';

interface Props {
  children: ReactNode;
  /** Fallback custom; se omitido, usa a tela de erro padrão. */
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    reportError(error, { componentStack: info.componentStack });
  }

  private handleRetry = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return <>{this.props.fallback}</>;

    return (
      <div className="min-h-[60vh] w-full flex flex-col items-center justify-center gap-4 px-6 py-10 text-center">
        <AlertTriangle size={40} strokeWidth={1.5} className="text-text3" />
        <div className="text-lg font-bold text-navy">Algo deu errado</div>
        <div className="text-sm text-text3 max-w-md leading-relaxed">
          Ocorreu um erro inesperado nesta tela. Tente recarregar; se persistir, avise o suporte.
        </div>
        <pre className="max-w-lg w-full overflow-auto rounded-md bg-bg2 border border-border px-3 py-2 text-xs text-text2 text-left">
          {error.message}
        </pre>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-blue px-4.5 py-2 text-sm font-bold text-white cursor-pointer hover:bg-blue2"
          >
            <span className="inline-flex items-center gap-1.5"><RefreshCw size={13} /> Recarregar</span>
          </button>
          <button
            type="button"
            onClick={this.handleRetry}
            className="rounded-lg border border-border bg-white px-4.5 py-2 text-sm font-bold text-text2 cursor-pointer hover:bg-bg2"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
